import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Download, Copy, Share2, RefreshCw, Trash2, Eye, Plus, Search,
} from "lucide-react";

const SIZES = [
  { key: "square",    label: "Square 1080x1080" },
  { key: "portrait",  label: "Portrait 1080x1350" },
  { key: "story",     label: "Story 1080x1920" },
  { key: "landscape", label: "Landscape 1200x630" },
];
const LAYOUTS = [
  { key: 1, label: "Display + Quote Marks" },
  { key: 2, label: "Italic Serif" },
  { key: 3, label: "Display + Gold Rules" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function abs(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND}${u.startsWith("/") ? "" : "/"}${u}`;
}

export default function AdminTruthCards() {
  const [truths, setTruths] = useState([]);
  const [cards, setCards] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);   // a truth obj
  const [attribution, setAttribution] = useState("");
  const [layout, setLayout] = useState(1);
  const [previewSize, setPreviewSize] = useState("square");
  const [previewBust, setPreviewBust] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [tw, cs] = await Promise.all([
      http.get("/admin/truth-wall/submissions?status=approved").catch(() => ({ data: [] })),
      http.get("/admin/truth-cards").catch(() => ({ data: [] })),
    ]);
    setTruths(tw.data || []);
    setCards(cs.data || []);
  };

  useEffect(() => { load(); }, []);

  const filteredTruths = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return truths.slice(0, 50);
    return truths
      .filter((t) => `${t.text || ""} ${t.name || ""} ${t.city || ""}`.toLowerCase().includes(needle))
      .slice(0, 50);
  }, [truths, q]);

  // Live preview from selected + tweaks
  const previewSrc = useMemo(() => {
    if (!selected) return "";
    const params = new URLSearchParams({
      _t: String(previewBust),
    }).toString();
    // Use a POST to /admin/truth-cards/preview.png via fetch into a blob URL
    return `live::${params}`; // placeholder - we'll fetch below
  }, [selected, previewBust]);

  const [previewBlob, setPreviewBlob] = useState("");
  useEffect(() => {
    let aborted = false;
    if (!selected) { setPreviewBlob(""); return; }
    (async () => {
      try {
        const resp = await http.post("/admin/truth-cards/preview.png", {
          quote: selected.text || "",
          byline: selected.name || "Simple Truths Club",
          attribution,
          layout,
          size: previewSize,
        }, { responseType: "blob" });
        if (aborted) return;
        const url = URL.createObjectURL(resp.data);
        setPreviewBlob(url);
      } catch (e) {
        toast.error("Preview failed.");
      }
    })();
    return () => { aborted = true; };
  }, [selected, attribution, layout, previewSize, previewBust]);

  const create = async () => {
    if (!selected) { toast.error("Select a truth first."); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/admin/truth-cards", {
        truth_id: selected.id,
        attribution: attribution.trim(),
        layout,
      });
      toast.success("Card saved. Use the buttons below to download/share.");
      setCards([data, ...cards]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const regen = async (cid, nextLayout) => {
    try {
      const { data } = await http.patch(`/admin/truth-cards/${cid}`, { layout: nextLayout });
      setCards(cards.map((c) => (c.id === cid ? data : c)));
      toast.success("Layout regenerated.");
    } catch (e) {
      toast.error("Regenerate failed.");
    }
  };

  const removeCard = async (cid) => {
    if (!window.confirm("Delete this card?")) return;
    try {
      await http.delete(`/admin/truth-cards/${cid}`);
      setCards(cards.filter((c) => c.id !== cid));
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const downloadPng = async (cid, size) => {
    try {
      const resp = await http.get(`/truth-cards/${cid}/image.png?size=${size}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stc-truth-card-${cid.slice(0, 8)}-${size}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed.");
    }
  };

  const copyCaption = async (c) => {
    const cap = `"${c.quote}"\n\n— ${c.byline}${c.attribution ? `\n${c.attribution}` : ""}\n\nsimpletruthsclub.com  ·  every truth has a home`;
    try {
      await navigator.clipboard.writeText(cap);
      toast.success("Caption copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const copyShare = async (c) => {
    const url = `${window.location.origin}${c.share_url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-truth-cards">
      <div>
        <h2 className="font-display text-2xl text-[#F4F1EA] mb-2 flex items-center gap-2">
          <Sparkles size={18} className="text-[#C9A055]" /> Truth Card Generator
        </h2>
        <p className="font-body text-[13px] text-[#8a857d] max-w-2xl">
          Turn any Truth Wall entry into branded social quote cards. Typography-only — no new artwork is generated.
          Each card produces square, portrait, story, and landscape sizes you can post anywhere.
        </p>
      </div>

      {/* ---- Generator ---- */}
      <div className="grid lg:grid-cols-[1fr_460px] gap-6">
        {/* Selector + controls */}
        <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-5">
          <div>
            <Label>Select a Truth Wall entry</Label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-3 text-[#8a857d]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by text, name, or city…"
                data-testid="tc-search"
                className="stc-input pl-9"
              />
            </div>
            <div className="mt-3 max-h-[280px] overflow-auto border border-white/10 divide-y divide-white/5">
              {filteredTruths.length === 0 && (
                <div className="p-4 text-[12px] text-[#8a857d] text-center">No matching approved truths.</div>
              )}
              {filteredTruths.map((t) => {
                const active = selected?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelected(t)}
                    data-testid={`tc-pick-${t.id}`}
                    className={`w-full text-left p-3 transition ${active ? "bg-[#C9A055]/10 border-l-2 border-[#C9A055]" : "hover:bg-white/[0.03] border-l-2 border-transparent"}`}
                  >
                    <div className="font-display text-[14px] text-[#F4F1EA] line-clamp-2">&ldquo;{t.text}&rdquo;</div>
                    <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">
                      {t.name || "Anonymous"}{t.city ? ` · ${t.city}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Attribution (optional)</Label>
              <input
                value={attribution}
                onChange={(e) => setAttribution(e.target.value)}
                placeholder='Adapted from "THE BELL"'
                data-testid="tc-attr"
                className="stc-input"
              />
              <p className="font-body text-[11px] text-[#8a857d] mt-1">Shows as small italic line above footer.</p>
            </div>
            <div>
              <Label>Layout</Label>
              <select
                value={layout}
                onChange={(e) => setLayout(Number(e.target.value))}
                data-testid="tc-layout"
                className="stc-input"
              >
                {LAYOUTS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setPreviewBust(previewBust + 1)}
                className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] hover:underline mt-2 flex items-center gap-1"
                data-testid="tc-refresh-preview"
              >
                <RefreshCw size={11} /> Refresh preview
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={create}
              disabled={!selected || busy}
              data-testid="tc-create"
              className="btn-gold flex items-center gap-2 disabled:opacity-50"
            >
              <Plus size={14} /> {busy ? "Saving…" : "Save card"}
            </button>
            {selected && (
              <button
                onClick={() => { setSelected(null); setAttribution(""); setLayout(1); }}
                className="btn-outline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-[#0a0a0a] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-3">
            <Label className="!mb-0">Live preview</Label>
            <select
              value={previewSize}
              onChange={(e) => setPreviewSize(e.target.value)}
              data-testid="tc-preview-size"
              className="stc-input !w-auto !py-1 !text-[11px]"
            >
              {SIZES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="bg-black border border-white/5 aspect-square flex items-center justify-center overflow-hidden">
            {previewBlob ? (
              <img src={previewBlob} alt="Card preview" className="max-w-full max-h-full object-contain" data-testid="tc-preview-img" />
            ) : (
              <div className="font-body text-[12px] text-[#8a857d] p-6 text-center">
                Select a truth on the left to preview.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- Saved cards ---- */}
      <div>
        <h3 className="font-display text-xl text-[#F4F1EA] mb-3">Saved Cards ({cards.length})</h3>
        {cards.length === 0 ? (
          <div className="border border-dashed border-white/10 p-8 text-center text-[13px] text-[#8a857d]">
            No saved cards yet. Pick a truth above and click <b>Save card</b>.
          </div>
        ) : (
          <div className="space-y-4">
            {cards.map((c) => (
              <SavedCardRow
                key={c.id}
                c={c}
                onRegen={(layout) => regen(c.id, layout)}
                onDelete={() => removeCard(c.id)}
                onDownload={(size) => downloadPng(c.id, size)}
                onCopyCaption={() => copyCaption(c)}
                onCopyShare={() => copyShare(c)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SavedCardRow({ c, onRegen, onDelete, onDownload, onCopyCaption, onCopyShare }) {
  const [bust, setBust] = useState(0);
  const [openSize, setOpenSize] = useState(null);
  const refresh = () => setBust(bust + 1);
  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-4" data-testid={`tc-row-${c.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] text-[#F4F1EA] line-clamp-2">&ldquo;{c.quote}&rdquo;</div>
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">
            {c.byline}{c.attribution ? ` · ${c.attribution}` : ""}
          </div>
        </div>
        <button onClick={onDelete} className="text-[#8a857d] hover:text-red-400" data-testid={`tc-del-${c.id}`}>
          <Trash2 size={15} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {SIZES.map((s) => (
          <div key={s.key} className="bg-black border border-white/10">
            <div className="aspect-square flex items-center justify-center overflow-hidden">
              <img
                src={`${abs(c.image_urls[s.key])}&_=${bust}`}
                alt={s.key}
                className="max-w-full max-h-full object-contain"
                data-testid={`tc-thumb-${c.id}-${s.key}`}
              />
            </div>
            <div className="flex items-center justify-between px-2 py-1 border-t border-white/10">
              <span className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d]">{s.key}</span>
              <div className="flex gap-1">
                <button title="Preview" onClick={() => setOpenSize(s.key)} className="text-[#8a857d] hover:text-[#C9A055]" data-testid={`tc-prev-${c.id}-${s.key}`}><Eye size={12} /></button>
                <button title="Download PNG" onClick={() => onDownload(s.key)} className="text-[#8a857d] hover:text-[#C9A055]" data-testid={`tc-dl-${c.id}-${s.key}`}><Download size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={onCopyCaption} className="btn-outline text-[11px] flex items-center gap-1.5" data-testid={`tc-cap-${c.id}`}>
          <Copy size={12} /> Copy Caption
        </button>
        <button onClick={onCopyShare} className="btn-outline text-[11px] flex items-center gap-1.5" data-testid={`tc-share-${c.id}`}>
          <Share2 size={12} /> Share Link
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">Regenerate:</span>
          {LAYOUTS.map((l) => (
            <button
              key={l.key}
              onClick={() => { onRegen(l.key); setTimeout(refresh, 600); }}
              data-testid={`tc-regen-${c.id}-${l.key}`}
              className={`font-ui text-[10px] tracking-[0.22em] uppercase px-2 py-1 border transition ${c.layout === l.key ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`}
            >
              {l.key}
            </button>
          ))}
        </div>
      </div>

      {openSize && (
        <div
          className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center p-6"
          onClick={() => setOpenSize(null)}
          data-testid={`tc-lightbox-${c.id}`}
        >
          <img src={abs(c.image_urls[openSize])} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function Label({ children, className = "" }) {
  return (
    <div className={`font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-2 ${className}`}>
      {children}
    </div>
  );
}
